// src/modules/store.ts
import { migrate, CURRENT_SCHEMA_VERSION } from './migrations.js';
import type { Answer, HistoryEntry, Prefs, ScoreResult, SessionState } from '../types/index.js';

const KEY_SESSION = 'vark.session.current';
const KEY_HISTORY = 'vark.history';
const KEY_PREFS = 'vark.prefs';
const HISTORY_MAX = 5;
const QUESTION_COUNT = 16;

function emptyAnswers(): Answer[] {
  return Array.from({ length: QUESTION_COUNT }, (_, i) => ({
    questionId: i + 1,
    selected: [] as string[],
  }));
}

function safeLoad<T>(key: string): T | null {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    return migrate<T>(JSON.parse(raw) as T);
  } catch {
    return null;
  }
}

export class Store {
  persistFailed: boolean;
  state: SessionState;
  history: HistoryEntry[];
  prefs: Prefs;

  constructor() {
    this.persistFailed = false;
    this.state = safeLoad<SessionState>(KEY_SESSION) || this._idle();
    this.history = safeLoad<HistoryEntry[]>(KEY_HISTORY) || [];
    this.prefs = safeLoad<Prefs>(KEY_PREFS)
      || { schemaVersion: CURRENT_SCHEMA_VERSION, cookieConsent: false };
  }

  private _idle(): SessionState {
    return {
      schemaVersion: CURRENT_SCHEMA_VERSION,
      status: 'idle',
      questionnaire: null,
      startedAt: null,
      completedAt: null,
      answers: emptyAnswers(),
      currentIndex: 0,
      result: null,
      resumeToken: null,
    };
  }

  private _persist(key: string, data: unknown): void {
    try {
      localStorage.setItem(key, JSON.stringify(data));
    } catch {
      this.persistFailed = true;
    }
  }

  startSession(questionnaire: 'A' | 'B'): void {
    if (!['A', 'B'].includes(questionnaire)) {
      throw new Error(`bad questionnaire ${questionnaire}`);
    }
    this.state = {
      ...this._idle(),
      status: 'in_progress',
      questionnaire,
      startedAt: new Date().toISOString(),
    };
    this._persist(KEY_SESSION, this.state);
  }

  setAnswer(questionId: number, selected: string[]): void {
    const idx = this.state.answers.findIndex(a => a.questionId === questionId);
    if (idx < 0) throw new Error(`bad questionId ${questionId}`);
    const prev = this.state.answers[idx]!.selected;
    if (prev.length === selected.length && prev.every((v, i) => v === selected[i])) return;
    this.state.answers[idx] = { questionId, selected };
    this._persist(KEY_SESSION, this.state);
  }

  setCurrentIndex(idx: number): void {
    if (idx === this.state.currentIndex) return;
    this.state.currentIndex = idx;
    this._persist(KEY_SESSION, this.state);
  }

  setResumeToken(token: string | null): void {
    if (token === this.state.resumeToken) return;
    this.state.resumeToken = token;
    this._persist(KEY_SESSION, this.state);
  }

  isComplete(): boolean {
    return this.state.answers.every(a => a.selected.length > 0);
  }

  completeSession(result: ScoreResult): void {
    this.state.status = 'completed';
    this.state.completedAt = new Date().toISOString();
    this.state.result = result;
    if (this.state.questionnaire === null) {
      throw new Error('cannot complete session: questionnaire is null');
    }
    this.history.unshift({
      id: crypto.randomUUID(),
      completedAt: this.state.completedAt,
      questionnaire: this.state.questionnaire,
      answers: this.state.answers,
      result,
    });
    this.history = this.history.slice(0, HISTORY_MAX);
    this._persist(KEY_HISTORY, this.history);
    this.state = this._idle();
    this._persist(KEY_SESSION, this.state);
  }

  loadFromSnapshot(snapshot: unknown): void {
    this.state = (migrate<SessionState>(snapshot as SessionState)) || this._idle();
    this._persist(KEY_SESSION, this.state);
  }
}
